import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
    AnalyzedRecipeTimeline,
    Language,
    PlannedMeal,
    PrepTimelineMode,
    Recipe,
    ScheduledPrepSegment,
    ScheduledRecipeTimeline,
} from '../types';
import { analyzePrepTimeline } from '../services/geminiService';
import type { PrepTimelineAnalysisError } from '../services/geminiService';
import {
    getTimelineSegmentSessionKey,
    isTimelineSegmentComplete,
} from '../utils/cookingSessionSteps';
import {
    applyInstructionDurationsToTimeline,
    buildFallbackTimeline,
    buildPreparedMealTimeline,
    flattenInstructionSteps,
    formatDurationLabel,
    formatTimelineDateTime,
    formatTimelineMinutes,
    getCurrentTimeLinePercent,
    getCurrentTimeMarkerStyle,
    getDayBoundaryNotice,
    getRecipeTimelineColor,
    getTimelineSegmentBarStyle,
    isOvernightPrepRecipe,
    isPreparedPlannedMeal,
    TIMELINE_WAIT_HATCH,
    parseDateTimeLocalValue,
    schedulePrepTimeline,
    TIMELINE_LABEL_COLUMN,
    toDateTimeLocalValue,
} from '../utils/prepTimeline';
import { getTranslation, isRTL } from '../utils/translations';
import XIcon from './icons/XIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ClockIcon from './icons/ClockIcon';
import SparklesIcon from './icons/SparklesIcon';
import CrescentMoonIcon from './icons/CrescentMoonIcon';
import ConfirmationModal from './ConfirmationModal';
import SmartUnitCapsule from './SmartUnitCapsule';

const EMPTY_STEP_SET: ReadonlySet<string> = new Set();

const clientTimelineCache = new Map<string, AnalyzedRecipeTimeline[]>();
const clientTimelineLoads = new Map<string, Promise<{ timelines: AnalyzedRecipeTimeline[]; analysisError: string | null; cacheable: boolean }>>();

function getTimelineAnalysisErrorMessage(
    failure: PrepTimelineAnalysisError | undefined,
    t: (key: Parameters<typeof getTranslation>[1]) => string
): string {
    if (failure === 'invalid_user_key') return t('prepTimelineFallbackInvalidKey');
    if (failure === 'rate_limited') return t('prepTimelineFallbackRateLimited');
    return t('prepTimelineFallbackNotice');
}

const FRAME_LINE = '#CBA177';
const INK = '#2C241B';
const INK_MUTED = '#6E6152';
const DONE_SAGE = '#7B9E89';

const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

interface PrepTimelineModalProps {
    isOpen: boolean;
    onClose: () => void;
    recipes: (PlannedMeal & { recipe: Recipe })[];
    language?: Language;
    encryptedGeminiKey?: string;
    mealLabel?: string;
    cookingStartedAt?: Date | null;
    onStartCooking?: (startTime: Date) => Promise<void>;
    onResetStartTime?: (startTime: Date) => Promise<void>;
    onStopSession?: () => Promise<void>;
    savedChartRecipeIds?: string[];
    onChartRecipeSelectionChange?: (selectedIds: string[]) => void | Promise<void>;
    savedChartRecipeOffsets?: Record<string, number>;
    onChartRecipeOffsetsChange?: (offsets: Record<string, number>) => void | Promise<void>;
    completedSteps?: ReadonlySet<string>;
    completedRecipes?: ReadonlySet<string>;
    onToggleStepComplete?: (stepKey: string) => void | Promise<void>;
}

function extractInstructionSteps(recipe: Recipe, defaultSection: string): { content: string; sectionHeader: string; durationMinutes?: number }[] {
    return flattenInstructionSteps(recipe.instructions, defaultSection);
}

function countInstructionSteps(recipe: Recipe): number {
    return Math.max(extractInstructionSteps(recipe, 'Prep').length, 1);
}

function defaultTargetTime(): string {
    const date = new Date();
    date.setHours(date.getHours() + 2);
    date.setMinutes(Math.ceil(date.getMinutes() / 15) * 15);
    date.setSeconds(0, 0);
    return toDateTimeLocalValue(date);
}

interface StartTimePickerModalProps {
    isOpen: boolean;
    title: string;
    value: string;
    saveLabel: string;
    cancelLabel: string;
    onChange: (value: string) => void;
    onSave: () => void;
    onCancel: () => void;
}

const StartTimePickerModal: React.FC<StartTimePickerModalProps> = ({
    isOpen,
    title,
    value,
    saveLabel,
    cancelLabel,
    onChange,
    onSave,
    onCancel,
}) => {
    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[650] p-0 sm:p-4 animate-fade-in"
            onClick={onCancel}
        >
            <div
                className="bg-white dark:bg-slate-900 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl border border-stone-100 dark:border-slate-800 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                    <h3 className="text-lg font-black text-slate-800 dark:text-slate-100">{title}</h3>
                </div>
                <div className="p-6">
                    <input
                        type="datetime-local"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full rounded-xl border px-3 py-2.5 text-sm bg-white dark:bg-slate-800 dark:border-slate-700"
                        style={{ borderColor: FRAME_LINE, color: INK }}
                    />
                </div>
                <div className="px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2 flex gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 py-3 bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 font-black rounded-full hover:bg-slate-50 dark:hover:bg-slate-700 transition-all uppercase tracking-widest text-xs"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        className="flex-1 py-3 bg-brand-sage text-white font-black rounded-full shadow-lg shadow-brand-sage/20 hover:opacity-90 transition-all uppercase tracking-widest text-xs"
                    >
                        {saveLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PrepTimelineModal: React.FC<PrepTimelineModalProps> = ({
    isOpen,
    onClose,
    recipes,
    language,
    encryptedGeminiKey,
    mealLabel,
    cookingStartedAt = null,
    onStartCooking,
    onResetStartTime,
    onStopSession,
    savedChartRecipeIds,
    onChartRecipeSelectionChange,
    savedChartRecipeOffsets,
    onChartRecipeOffsetsChange,
    completedSteps,
    completedRecipes,
    onToggleStepComplete,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const rtl = isRTL(language);

    const isSessionActive = cookingStartedAt !== null;
    const [mode, setMode] = useState<PrepTimelineMode>('startNow');
    const [targetTimeValue, setTargetTimeValue] = useState(defaultTargetTime);
    const [startTimeValue, setStartTimeValue] = useState(() => toDateTimeLocalValue(new Date()));
    const [isAdjustStartChoiceOpen, setIsAdjustStartChoiceOpen] = useState(false);
    const [startTimePickerPurpose, setStartTimePickerPurpose] = useState<'planning' | 'session' | null>(null);
    const [startTimeDraft, setStartTimeDraft] = useState(() => toDateTimeLocalValue(new Date()));
    const [isStopConfirmOpen, setIsStopConfirmOpen] = useState(false);
    const [isSavingSession, setIsSavingSession] = useState(false);
    const [analyzedRecipes, setAnalyzedRecipes] = useState<AnalyzedRecipeTimeline[]>([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisError, setAnalysisError] = useState<string | null>(null);
    const [expandedRecipes, setExpandedRecipes] = useState<Set<string>>(new Set());
    const [selectedRecipeIds, setSelectedRecipeIds] = useState<Set<string>>(new Set());
    const [recipeOffsets, setRecipeOffsets] = useState<Record<string, number>>({});
    const [cacheKey, setCacheKey] = useState('');
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        if (!isOpen) return;
        setNow(new Date());
        const interval = window.setInterval(() => setNow(new Date()), 30_000);
        return () => window.clearInterval(interval);
    }, [isOpen]);

    const recipeInputs = useMemo(
        () => recipes.map((mr) => ({
            id: mr.recipe.id,
            name: mr.recipe.name,
            prepTime: mr.recipe.prepTime,
            instructions: mr.recipe.instructions,
            isPrepared: isPreparedPlannedMeal(mr.sourcingStatus),
            steps: extractInstructionSteps(mr.recipe, t('prepTab')),
            stepCount: countInstructionSteps(mr.recipe),
        })),
        [recipes, t]
    );

    const nextCacheKey = useMemo(
        () => recipeInputs.map((r) => {
            const durationKey = r.steps.map((step) => step.durationMinutes ?? '').join(',');
            return `${r.id}:${r.prepTime}:${r.stepCount}:${durationKey}:${r.isPrepared ? 'prepared' : 'full'}`;
        }).join('|'),
        [recipeInputs]
    );

    const analyzedRecipeIdsKey = useMemo(
        () => analyzedRecipes.map((recipe) => recipe.recipeId).join('|'),
        [analyzedRecipes]
    );

    const savedSelectionKey = savedChartRecipeIds?.join('|') ?? '';
    const savedOffsetsKey = useMemo(
        () => Object.entries(savedChartRecipeOffsets ?? {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([id, minutes]) => `${id}:${minutes}`)
            .join('|'),
        [savedChartRecipeOffsets]
    );

    useEffect(() => {
        if (!isOpen) return;
        if (savedChartRecipeOffsets) {
            setRecipeOffsets(savedChartRecipeOffsets);
        }
    }, [isOpen, savedOffsetsKey, savedChartRecipeOffsets]);

    const offsetAnchorRef = useRef<'target' | 'start'>(mode === 'targetTime' ? 'target' : 'start');
    useEffect(() => {
        if (!isOpen) return;
        const anchor: 'target' | 'start' = mode === 'targetTime' ? 'target' : 'start';
        if (offsetAnchorRef.current === anchor) return;
        offsetAnchorRef.current = anchor;
        setRecipeOffsets({});
        void onChartRecipeOffsetsChange?.({});
    }, [isOpen, mode, onChartRecipeOffsetsChange]);

    const loadTimeline = useCallback(async () => {
        if (!recipeInputs.length) {
            setAnalyzedRecipes([]);
            return;
        }

        const clientCacheKey = `${language || 'en'}|${nextCacheKey}`;
        const cachedTimelines = clientTimelineCache.get(clientCacheKey);
        if (cachedTimelines) {
            setAnalyzedRecipes(cachedTimelines);
            setAnalysisError(null);
            setCacheKey(nextCacheKey);
            return;
        }

        setIsAnalyzing(true);
        setAnalysisError(null);

        const buildTimelines = async (): Promise<{
            timelines: AnalyzedRecipeTimeline[];
            analysisError: string | null;
            cacheable: boolean;
        }> => {
            const preparedTimelines = recipeInputs
                .filter((recipe) => recipe.isPrepared)
                .map((recipe) => buildPreparedMealTimeline(recipe.id, recipe.name, t('prepTimelineHeatDefrost')));

            const fullPrepInputs = recipeInputs.filter((recipe) => !recipe.isPrepared);
            let fullTimelines: AnalyzedRecipeTimeline[] = [];
            let analysisFailure: PrepTimelineAnalysisError | undefined;
            let aiSucceeded = fullPrepInputs.length === 0;

            if (fullPrepInputs.length > 0) {
                const { data: aiResult, error } = await analyzePrepTimeline(
                    fullPrepInputs.map(({ id, name, prepTime, instructions }) => ({ id, name, prepTime, instructions })),
                    language || 'en',
                    encryptedGeminiKey
                );
                analysisFailure = error;
                aiSucceeded = Boolean(aiResult?.recipes?.length);

                if (aiSucceeded) {
                    fullTimelines = aiResult.recipes.map((timeline) => {
                        const mealRecipe = recipes.find((mr) => mr.recipe.id === timeline.recipeId);
                        if (!mealRecipe) return timeline;
                        const flatSteps = flattenInstructionSteps(mealRecipe.recipe.instructions, t('prepTab'));
                        return applyInstructionDurationsToTimeline(timeline, flatSteps);
                    });
                } else {
                    fullTimelines = buildFallbackTimeline(
                        fullPrepInputs.map(({ id, name, prepTime, steps }) => ({ id, name, prepTime, steps }))
                    );
                }
            }

            const timelineById = new Map<string, AnalyzedRecipeTimeline>(
                [...preparedTimelines, ...fullTimelines].map((recipe) => [recipe.recipeId, recipe])
            );
            const timelines = recipeInputs
                .map((recipe) => timelineById.get(recipe.id))
                .filter((recipe): recipe is AnalyzedRecipeTimeline => recipe !== undefined);

            const usedAiFallback = fullPrepInputs.length > 0 && !aiSucceeded;
            return {
                timelines,
                analysisError: usedAiFallback ? getTimelineAnalysisErrorMessage(analysisFailure, t) : null,
                cacheable: !usedAiFallback,
            };
        };

        let loadPromise = clientTimelineLoads.get(clientCacheKey);
        if (!loadPromise) {
            loadPromise = buildTimelines().finally(() => {
                clientTimelineLoads.delete(clientCacheKey);
            });
            clientTimelineLoads.set(clientCacheKey, loadPromise);
        }

        try {
            const { timelines, analysisError, cacheable } = await loadPromise;
            if (cacheable) {
                clientTimelineCache.set(clientCacheKey, timelines);
            }
            setAnalyzedRecipes(timelines);
            setAnalysisError(analysisError);
            setCacheKey(nextCacheKey);
        } finally {
            setIsAnalyzing(false);
        }
    }, [recipeInputs, recipes, language, encryptedGeminiKey, nextCacheKey, t]);

    useEffect(() => {
        if (!isOpen) return;
        if (cacheKey !== nextCacheKey) {
            void loadTimeline();
        }
    }, [isOpen, cacheKey, nextCacheKey, loadTimeline]);

    useEffect(() => {
        if (!isOpen) {
            setExpandedRecipes(new Set());
            setMode('startNow');
            setTargetTimeValue(defaultTargetTime());
            setStartTimeValue(toDateTimeLocalValue(new Date()));
            setIsAdjustStartChoiceOpen(false);
            setStartTimePickerPurpose(null);
            setIsStopConfirmOpen(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || analyzedRecipes.length === 0) return;

        const allIds = analyzedRecipes.map((recipe) => recipe.recipeId);

        if (savedChartRecipeIds !== undefined) {
            const validSaved = savedChartRecipeIds.filter((id) => allIds.includes(id));
            setSelectedRecipeIds(new Set(validSaved.length > 0 ? validSaved : [allIds[0]]));
            return;
        }

        setSelectedRecipeIds((prev) => {
            const validPrev = [...prev].filter((id) => allIds.includes(id));
            const newIds = allIds.filter((id) => !prev.has(id));
            const merged = [...validPrev, ...newIds];
            return new Set(merged.length > 0 ? merged : allIds);
        });
    }, [isOpen, analyzedRecipeIdsKey, savedSelectionKey, savedChartRecipeIds, analyzedRecipes]);

    useEffect(() => {
        if (cookingStartedAt) {
            setStartTimeDraft(toDateTimeLocalValue(cookingStartedAt));
        }
    }, [cookingStartedAt]);

    const parsedTargetTime = useMemo(
        () => (mode === 'targetTime' ? parseDateTimeLocalValue(targetTimeValue) : null),
        [mode, targetTimeValue]
    );

    const targetIsFuture = parsedTargetTime != null && parsedTargetTime.getTime() > now.getTime();
    const previewTargetAsSession = !isSessionActive && mode === 'targetTime' && targetIsFuture;

    const effectiveMode: PrepTimelineMode = isSessionActive || previewTargetAsSession ? 'session' : mode;

    const selectedAnalyzedRecipes = useMemo(
        () => analyzedRecipes.filter((recipe) => selectedRecipeIds.has(recipe.recipeId)),
        [analyzedRecipes, selectedRecipeIds]
    );

    const impliedSessionStart = useMemo(() => {
        if (isSessionActive && cookingStartedAt) return cookingStartedAt;
        if (!previewTargetAsSession || !parsedTargetTime) return null;

        const durationSource = selectedAnalyzedRecipes.length > 0 ? selectedAnalyzedRecipes : analyzedRecipes;
        const maxDuration = durationSource.length > 0
            ? Math.max(...durationSource.map((recipe) => recipe.totalElapsedMinutes))
            : 0;

        return new Date(parsedTargetTime.getTime() - maxDuration * 60_000);
    }, [isSessionActive, cookingStartedAt, previewTargetAsSession, parsedTargetTime, selectedAnalyzedRecipes, analyzedRecipes]);

    const scheduleOptions = useMemo(() => ({
        targetTime: effectiveMode === 'targetTime' ? parsedTargetTime ?? undefined : undefined,
        sessionStart: effectiveMode === 'session' ? impliedSessionStart ?? undefined : undefined,
        now,
        recipeOffsets,
    }), [effectiveMode, parsedTargetTime, impliedSessionStart, now, recipeOffsets]);

    const usesFinishEarlyOffset = mode === 'targetTime' && !previewTargetAsSession;

    const updateRecipeOffset = useCallback((recipeId: string, minutes: number) => {
        setRecipeOffsets((prev) => {
            const next = { ...prev };
            if (minutes <= 0) {
                delete next[recipeId];
            } else {
                next[recipeId] = minutes;
            }
            void onChartRecipeOffsetsChange?.(next);
            return next;
        });
    }, [onChartRecipeOffsetsChange]);

    const chartSchedule = useMemo(
        () => schedulePrepTimeline(
            selectedAnalyzedRecipes.length > 0 ? selectedAnalyzedRecipes : analyzedRecipes,
            effectiveMode,
            scheduleOptions
        ),
        [selectedAnalyzedRecipes, analyzedRecipes, effectiveMode, scheduleOptions]
    );

    const fullSchedule = useMemo(
        () => schedulePrepTimeline(analyzedRecipes, effectiveMode, scheduleOptions),
        [analyzedRecipes, effectiveMode, scheduleOptions]
    );

    const schedule = chartSchedule;

    const requiredStartTimeMs = chartSchedule.timelineStart.getTime();

    useEffect(() => {
        if (!isOpen || isSessionActive || mode !== 'targetTime') return;
        setStartTimeValue(toDateTimeLocalValue(new Date(requiredStartTimeMs)));
    }, [
        isOpen,
        isSessionActive,
        mode,
        requiredStartTimeMs,
        targetTimeValue,
        analyzedRecipeIdsKey,
        savedSelectionKey,
    ]);

    useEffect(() => {
        if (!isOpen || isSessionActive || mode !== 'startNow') return;
        setStartTimeValue(toDateTimeLocalValue(new Date()));
    }, [isOpen, isSessionActive, mode]);

    const resolveStartTimeForCooking = useCallback((): Date => {
        if (mode === 'targetTime') {
            return new Date(requiredStartTimeMs);
        }
        return parseDateTimeLocalValue(startTimeValue);
    }, [mode, requiredStartTimeMs, startTimeValue]);

    const openStartTimePicker = (purpose: 'planning' | 'session') => {
        const initialValue = purpose === 'planning'
            ? startTimeValue
            : toDateTimeLocalValue(cookingStartedAt ?? new Date());
        setStartTimeDraft(initialValue);
        setStartTimePickerPurpose(purpose);
        setIsAdjustStartChoiceOpen(false);
    };

    const handleStartCooking = async () => {
        if (!onStartCooking) return;
        setIsSavingSession(true);
        try {
            await onStartCooking(resolveStartTimeForCooking());
        } finally {
            setIsSavingSession(false);
        }
    };

    const handleSaveStartTime = async () => {
        if (startTimePickerPurpose === 'planning') {
            setStartTimeValue(startTimeDraft);
            setStartTimePickerPurpose(null);
            return;
        }

        if (startTimePickerPurpose === 'session' && onResetStartTime) {
            setIsSavingSession(true);
            try {
                await onResetStartTime(parseDateTimeLocalValue(startTimeDraft));
                setStartTimePickerPurpose(null);
            } finally {
                setIsSavingSession(false);
            }
        }
    };

    const handleResetStartToNow = async () => {
        if (!onResetStartTime) return;
        setIsSavingSession(true);
        try {
            await onResetStartTime(new Date());
            setIsAdjustStartChoiceOpen(false);
        } finally {
            setIsSavingSession(false);
        }
    };

    const dayBoundaryNotice = useMemo(
        () => getDayBoundaryNotice(chartSchedule, effectiveMode, now),
        [chartSchedule, effectiveMode, now]
    );

    const currentTimeLinePercent = useMemo(
        () => getCurrentTimeLinePercent(chartSchedule, now),
        [chartSchedule, now]
    );

    const scheduledById = useMemo(() => {
        const map = new Map<string, ScheduledRecipeTimeline>();
        fullSchedule.recipes.forEach((recipe) => map.set(recipe.recipeId, recipe));
        return map;
    }, [fullSchedule]);

    const chartScheduledById = useMemo(() => {
        const map = new Map<string, ScheduledRecipeTimeline>();
        chartSchedule.recipes.forEach((recipe) => map.set(recipe.recipeId, recipe));
        return map;
    }, [chartSchedule]);

    const toggleRecipeSelected = (recipeId: string) => {
        setSelectedRecipeIds((prev) => {
            const next = new Set(prev);
            if (next.has(recipeId)) {
                if (next.size <= 1) return prev;
                next.delete(recipeId);
            } else {
                next.add(recipeId);
            }
            void onChartRecipeSelectionChange?.([...next]);
            return next;
        });
    };

    const toggleRecipeExpanded = (recipeId: string) => {
        setExpandedRecipes((prev) => {
            const next = new Set(prev);
            if (next.has(recipeId)) next.delete(recipeId);
            else next.add(recipeId);
            return next;
        });
    };

    const timeTicks = useMemo(() => {
        const span = Math.max(chartSchedule.totalSpanMinutes, 30);
        const tickCount = Math.min(8, Math.max(4, Math.ceil(span / 30)));
        const step = span / tickCount;
        return Array.from({ length: tickCount + 1 }, (_, index) => Math.round(index * step));
    }, [chartSchedule.totalSpanMinutes]);

    const chartGridStyle = useMemo(
        () => ({ gridTemplateColumns: `${TIMELINE_LABEL_COLUMN} 1fr` }),
        []
    );

    const chartDir = rtl ? 'rtl' : 'ltr';

    const analyzedRecipeById = useMemo(() => {
        const map = new Map<string, AnalyzedRecipeTimeline>();
        analyzedRecipes.forEach((recipe) => map.set(recipe.recipeId, recipe));
        return map;
    }, [analyzedRecipes]);

    const recipeById = useMemo(() => {
        const map = new Map<string, Recipe>();
        recipes.forEach((mealRecipe) => map.set(mealRecipe.recipe.id, mealRecipe.recipe));
        return map;
    }, [recipes]);

    const prepTabLabel = t('prepTab');

    const resolveSegmentSessionKey = useCallback((segment: ScheduledPrepSegment): string | null => {
        return getTimelineSegmentSessionKey(
            segment,
            analyzedRecipeById.get(segment.recipeId),
            recipeById.get(segment.recipeId),
            prepTabLabel
        );
    }, [analyzedRecipeById, recipeById, prepTabLabel]);

    const isSegmentComplete = useCallback((segment: ScheduledPrepSegment): boolean => {
        const sessionStepKey = resolveSegmentSessionKey(segment);
        return isTimelineSegmentComplete(
            segment.recipeId,
            sessionStepKey,
            completedSteps ?? EMPTY_STEP_SET,
            completedRecipes ?? EMPTY_STEP_SET
        );
    }, [resolveSegmentSessionKey, completedSteps, completedRecipes]);

    const handleStopSession = async () => {
        if (!onStopSession) return;
        setIsSavingSession(true);
        try {
            await onStopSession();
            setIsStopConfirmOpen(false);
        } finally {
            setIsSavingSession(false);
        }
    };

    const spanMinutes = Math.max(chartSchedule.totalSpanMinutes, 1);

    const timeTickItems = useMemo(() => {
        return timeTicks.map((tick) => ({
            key: tick,
            label: formatTimelineMinutes(tick, chartSchedule.timelineStart),
        }));
    }, [timeTicks, chartSchedule.timelineStart]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[120] flex flex-col bg-stone-200/95 dark:bg-slate-950/95 backdrop-blur-sm"
            dir={rtl ? 'rtl' : 'ltr'}
        >
            <div className="flex-shrink-0 border-b border-stone-300/80 dark:border-slate-800 bg-[#FDFBF7] dark:bg-slate-900 px-4 py-3">
                <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="text-lg font-bold truncate" style={{ color: INK }}>
                            {t('prepTimelineTitle')}
                        </h2>
                        {mealLabel && (
                            <p className="text-xs truncate" style={{ color: INK_MUTED }}>{mealLabel}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full transition-colors hover:bg-stone-100 dark:hover:bg-slate-800"
                        style={{ color: FRAME_LINE }}
                        aria-label={t('close')}
                    >
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>
            </div>

            <div className="flex-shrink-0 border-b border-stone-300/60 dark:border-slate-800 bg-[#FDFBF7] dark:bg-slate-900 px-4 py-3">
                <div className="max-w-4xl mx-auto flex flex-col gap-3">
                    {isSessionActive ? (
                        <>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wide"
                                    style={{ borderColor: FRAME_LINE, color: INK }}>
                                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                    {t('prepTimelineSessionActive')}
                                </div>
                                <div className="text-sm font-medium" style={{ color: INK }}>
                                    {t('prepTimelineStartedAt').replace('{time}', formatTimelineDateTime(cookingStartedAt!))}
                                    {' · '}
                                    {t('prepTimelineFinishBy')} {formatTimelineDateTime(schedule.timelineEnd, now)}
                                </div>
                                <div className="sm:ms-auto flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsAdjustStartChoiceOpen(true)}
                                        disabled={isSavingSession}
                                        className="px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wide transition-colors hover:bg-stone-100 dark:hover:bg-slate-800 disabled:opacity-50"
                                        style={{ borderColor: FRAME_LINE, color: INK }}
                                    >
                                        {t('prepTimelineAdjustStartTime')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsStopConfirmOpen(true)}
                                        disabled={isSavingSession}
                                        className="px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wide transition-colors hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50"
                                        style={{ borderColor: '#DC2626', color: '#DC2626' }}
                                    >
                                        {t('prepTimelineStopSession')}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="inline-flex rounded-full border p-1" style={{ borderColor: FRAME_LINE }}>
                                    <button
                                        onClick={() => setMode('startNow')}
                                        className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
                                            mode === 'startNow'
                                                ? 'text-[#FDFBF7]'
                                                : 'text-stone-600 dark:text-slate-300'
                                        }`}
                                        style={mode === 'startNow' ? { backgroundColor: INK } : undefined}
                                    >
                                        {t('prepTimelineStartNow')}
                                    </button>
                                    <button
                                        onClick={() => setMode('targetTime')}
                                        className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
                                            mode === 'targetTime'
                                                ? 'text-[#FDFBF7]'
                                                : 'text-stone-600 dark:text-slate-300'
                                        }`}
                                        style={mode === 'targetTime' ? { backgroundColor: INK } : undefined}
                                    >
                                        {t('prepTimelineTargetTime')}
                                    </button>
                                </div>

                                {mode === 'targetTime' && (
                                    <label className="flex items-center gap-2 text-sm" style={{ color: INK }}>
                                        <ClockIcon className="w-4 h-4" style={{ color: FRAME_LINE }} />
                                        <span className="font-medium">{t('prepTimelineReadyBy')}</span>
                                        <input
                                            type="datetime-local"
                                            value={targetTimeValue}
                                            onChange={(e) => setTargetTimeValue(e.target.value)}
                                            className="rounded-lg border px-2 py-1 text-sm bg-white dark:bg-slate-800 dark:border-slate-700"
                                            style={{ borderColor: FRAME_LINE, color: INK }}
                                        />
                                    </label>
                                )}

                                <div className="sm:ms-auto text-xs" style={{ color: INK_MUTED }}>
                                    {mode === 'startNow'
                                        ? `${t('prepTimelineFinishBy')} ${formatTimelineDateTime(schedule.timelineEnd, now)}`
                                        : `${t('prepTimelineStartBy')} ${formatTimelineDateTime(schedule.timelineStart, now)}`}
                                </div>
                            </div>

                            {previewTargetAsSession && (
                                <p className="text-xs font-medium" style={{ color: FRAME_LINE }}>
                                    {t('prepTimelineTargetAssumedStarted').replace(
                                        '{time}',
                                        formatTimelineDateTime(new Date(requiredStartTimeMs), now)
                                    )}
                                </p>
                            )}

                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1 border-t border-stone-200/80 dark:border-slate-800">
                                {mode === 'startNow' && (
                                    <button
                                        type="button"
                                        onClick={() => openStartTimePicker('planning')}
                                        className="px-4 py-2 rounded-full border text-xs font-bold uppercase tracking-wide transition-colors hover:bg-stone-100 dark:hover:bg-slate-800"
                                        style={{ borderColor: FRAME_LINE, color: INK }}
                                    >
                                        {t('prepTimelineSetStartTime')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => void handleStartCooking()}
                                    disabled={isSavingSession || !onStartCooking}
                                    className="px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wide text-[#FDFBF7] disabled:opacity-50 transition-opacity hover:opacity-90 sm:ms-auto"
                                    style={{ backgroundColor: INK }}
                                >
                                    {t('prepTimelineStartCooking')}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {dayBoundaryNotice && (
                    <div className="max-w-4xl mx-auto mt-2 text-xs font-medium" style={{ color: FRAME_LINE }}>
                        {dayBoundaryNotice === 'startPreviousDay'
                            ? t('prepTimelineStartPreviousDay')
                            : t('prepTimelineReadyNextDay')}
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-6">
                <div className="max-w-4xl mx-auto">
                    {isAnalyzing ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <SparklesIcon className="w-10 h-10 mb-4 animate-pulse" style={{ color: FRAME_LINE }} />
                            <p className="font-medium" style={{ color: INK }}>{t('prepTimelineAnalyzing')}</p>
                            <p className="text-sm mt-1" style={{ color: INK_MUTED }}>{t('prepTimelineAnalyzingHint')}</p>
                        </div>
                    ) : (
                        <>
                            {analysisError && (
                                <div className="mb-4 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: FRAME_LINE, color: INK_MUTED }}>
                                    {analysisError}
                                </div>
                            )}

                            <div
                                className="rounded-2xl border bg-[#FDFBF7] dark:bg-slate-900 p-4 shadow-sm"
                                style={{ borderColor: FRAME_LINE }}
                            >
                                <div className="flex items-center justify-between mb-4 text-xs uppercase tracking-wider font-bold" style={{ color: INK_MUTED }}>
                                    <span>{t('prepTimelineChart')}</span>
                                    <span>{formatDurationLabel(spanMinutes, language)} {t('prepTimelineTotal')}</span>
                                </div>

                                <div className="overflow-x-auto" dir={chartDir}>
                                    <div className="relative min-w-[640px]">
                                        {currentTimeLinePercent !== null && (
                                            <div
                                                className="pointer-events-none absolute top-0 bottom-0 w-px z-20"
                                                style={{
                                                    ...getCurrentTimeMarkerStyle(currentTimeLinePercent, rtl),
                                                    backgroundColor: '#DC2626',
                                                }}
                                                aria-hidden
                                            />
                                        )}

                                        <div className="grid gap-3 mb-2 items-center" style={chartGridStyle}>
                                            <div />
                                            <div className="flex items-center">
                                                {timeTickItems.map((tick) => (
                                                    <div
                                                        key={tick.key}
                                                        className="flex-1 text-[10px] font-medium text-center"
                                                        style={{ color: INK_MUTED }}
                                                    >
                                                        {tick.label}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            {analyzedRecipes.map((recipe, recipeIndex) => {
                                                const scheduled = scheduledById.get(recipe.recipeId);
                                                const chartScheduled = chartScheduledById.get(recipe.recipeId);
                                                const isExpanded = expandedRecipes.has(recipe.recipeId);
                                                const isSelected = selectedRecipeIds.has(recipe.recipeId);
                                                const isOvernight = isOvernightPrepRecipe(recipe);
                                                const color = getRecipeTimelineColor(recipeIndex);

                                                return (
                                                    <div
                                                        key={recipe.recipeId}
                                                        className={`rounded-xl border overflow-hidden transition-opacity ${isSelected ? 'border-stone-200 dark:border-slate-800' : 'border-stone-200/60 dark:border-slate-800/60 opacity-50'}`}
                                                    >
                                                        <div
                                                            className={`w-full grid gap-3 px-3 py-3 items-start ${isSelected ? 'hover:bg-stone-50 dark:hover:bg-slate-800/50' : ''}`}
                                                            style={chartGridStyle}
                                                        >
                                                            <div className="flex items-start gap-2 min-w-0" dir={rtl ? 'rtl' : 'ltr'}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => toggleRecipeSelected(recipe.recipeId)}
                                                                    aria-label={`${t('prepTimelineIncludeInChart')}: ${recipe.recipeName}`}
                                                                    className="mt-1 h-4 w-4 rounded border-stone-300 text-brand-sage focus:ring-brand-sage/30 flex-shrink-0"
                                                                    style={{ accentColor: FRAME_LINE }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleRecipeExpanded(recipe.recipeId)}
                                                                    className="flex items-start gap-2 min-w-0 text-start flex-1"
                                                                >
                                                                    <ChevronDownIcon
                                                                        className={`w-4 h-4 flex-shrink-0 mt-0.5 transition-transform ${isExpanded ? '' : (rtl ? 'rotate-90' : '-rotate-90')}`}
                                                                        style={{ color: FRAME_LINE }}
                                                                    />
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                                            <div className="font-semibold text-sm leading-snug break-words" style={{ color: isSelected ? INK : INK_MUTED }}>
                                                                                {recipe.recipeName}
                                                                            </div>
                                                                            {isOvernight && (
                                                                                <span
                                                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide flex-shrink-0"
                                                                                    style={{ backgroundColor: 'rgba(139, 126, 200, 0.15)', color: '#6B5B95' }}
                                                                                >
                                                                                    <CrescentMoonIcon className="w-3 h-3" />
                                                                                    {t('prepTimelineOvernight')}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-[11px] mt-1 leading-snug break-words" style={{ color: INK_MUTED }}>
                                                                            {formatDurationLabel(recipe.totalElapsedMinutes, language)}
                                                                            {scheduled && effectiveMode === 'targetTime' && (
                                                                                <> · {t('prepTimelineStartAt')} {formatTimelineMinutes(scheduled.startMinutes, fullSchedule.timelineStart)}</>
                                                                            )}
                                                                            {scheduled && (effectiveMode === 'startNow' || effectiveMode === 'session') && (
                                                                                <> · {t('prepTimelineReadyAt')} {formatTimelineMinutes(scheduled.endMinutes, fullSchedule.timelineStart)}</>
                                                                            )}
                                                                        </div>
                                                                        {isSelected && (
                                                                            <div
                                                                                className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: INK_MUTED }}>
                                                                                    {usesFinishEarlyOffset ? t('prepTimelineFinishEarly') : t('prepTimelineStartLater')}
                                                                                </span>
                                                                                <span className="text-[10px] font-bold tabular-nums" style={{ color: FRAME_LINE }}>
                                                                                    {usesFinishEarlyOffset ? '−' : '+'}
                                                                                </span>
                                                                                <SmartUnitCapsule
                                                                                    value={recipeOffsets[recipe.recipeId] ?? 0}
                                                                                    unit="mins"
                                                                                    availableUnits={['mins']}
                                                                                    onChange={(val) => updateRecipeOffset(recipe.recipeId, val)}
                                                                                    language={language}
                                                                                    step={15}
                                                                                    min={0}
                                                                                    max={usesFinishEarlyOffset
                                                                                        ? Math.max(0, recipe.totalElapsedMinutes - 15)
                                                                                        : 12 * 60}
                                                                                    floatWhenOpen
                                                                                    zIndex={400}
                                                                                    bgClass="!bg-white dark:!bg-slate-900"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </button>
                                                            </div>
                                                            <div className={`h-3 rounded-full relative overflow-hidden ${isSelected ? 'bg-stone-100 dark:bg-slate-800' : 'bg-stone-100/70 dark:bg-slate-800/40'}`} dir="ltr">
                                                                {isSelected && chartScheduled?.segments.map((segment, segmentIndex) => {
                                                                    const segmentDone = isSegmentComplete(segment);
                                                                    return (
                                                                        <div
                                                                            key={`summary-${segment.stepIndex}-${segment.kind}-${segmentIndex}`}
                                                                            className="absolute inset-y-0 rounded-full"
                                                                            style={getTimelineSegmentBarStyle(
                                                                                segment.startMinutes,
                                                                                segment.durationMinutes,
                                                                                spanMinutes,
                                                                                rtl,
                                                                                color,
                                                                                segment.kind,
                                                                                segmentDone
                                                                            )}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>

                                                        <div className="px-3 pb-3">
                                                            {isExpanded && chartScheduled && isSelected && (
                                                                <div className="space-y-2 pt-1 border-t border-stone-100 dark:border-slate-800">
                                                                    {chartScheduled.segments.map((segment, segmentIndex) => {
                                                                        const sessionStepKey = resolveSegmentSessionKey(segment);
                                                                        const segmentDone = isSegmentComplete(segment);
                                                                        const canToggle = Boolean(sessionStepKey && onToggleStepComplete);

                                                                        return (
                                                                            <div key={`${segment.stepIndex}-${segment.kind}-${segmentIndex}`} className="grid gap-3 items-start" style={chartGridStyle}>
                                                                                <div className="flex items-start gap-2 pt-1 px-1" dir={rtl ? 'rtl' : 'ltr'}>
                                                                                    {canToggle ? (
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => void onToggleStepComplete?.(sessionStepKey!)}
                                                                                            title={segmentDone ? t('markIncomplete') : t('markComplete')}
                                                                                            className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border transition-all active:scale-95 ${
                                                                                                segmentDone
                                                                                                    ? 'text-white border-transparent'
                                                                                                    : 'bg-white dark:bg-slate-900 border-stone-200 dark:border-slate-700 hover:bg-stone-50 dark:hover:bg-slate-800'
                                                                                            }`}
                                                                                            style={segmentDone ? { backgroundColor: DONE_SAGE } : { color: DONE_SAGE, borderColor: `${DONE_SAGE}33` }}
                                                                                        >
                                                                                            {segmentDone ? (
                                                                                                <CheckIcon className="w-4 h-4" />
                                                                                            ) : (
                                                                                                <span className="text-[10px] font-bold">{segment.stepIndex + 1}</span>
                                                                                            )}
                                                                                        </button>
                                                                                    ) : (
                                                                                        <div className="w-7 flex-shrink-0" aria-hidden />
                                                                                    )}
                                                                                    <div className={`text-[11px] leading-snug break-words ${segmentDone ? 'line-through opacity-60' : ''}`} style={{ color: INK_MUTED }}>
                                                                                        {formatTimelineMinutes(segment.startMinutes, chartSchedule.timelineStart)}
                                                                                        {' · '}
                                                                                        {formatDurationLabel(segment.durationMinutes, language)}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <div className="h-6 rounded-md relative overflow-hidden bg-stone-100 dark:bg-slate-800" dir="ltr">
                                                                                        <div
                                                                                            className="absolute inset-y-0 rounded-md"
                                                                                            style={getTimelineSegmentBarStyle(
                                                                                                segment.startMinutes,
                                                                                                segment.durationMinutes,
                                                                                                spanMinutes,
                                                                                                rtl,
                                                                                                color,
                                                                                                segment.kind,
                                                                                                segmentDone
                                                                                            )}
                                                                                        />
                                                                                    </div>
                                                                                    <p
                                                                                        className={`text-xs mt-1 leading-relaxed break-words ${segmentDone ? 'line-through opacity-60' : ''}`}
                                                                                        style={{ color: segmentDone ? INK_MUTED : INK }}
                                                                                        dir={rtl ? 'rtl' : 'ltr'}
                                                                                    >
                                                                                        {segment.kind === 'wait' && (
                                                                                            <span className="font-semibold uppercase tracking-wide me-1" style={{ color: INK_MUTED }}>
                                                                                                {t('prepTimelineWait')}
                                                                                            </span>
                                                                                        )}
                                                                                        {segment.content}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className={`mt-4 flex flex-wrap gap-4 text-xs ${rtl ? 'flex-row-reverse justify-end' : ''}`} style={{ color: INK_MUTED }}>
                                    <div className={`flex items-center gap-2 ${rtl ? 'flex-row-reverse' : ''}`}>
                                        <span className="inline-block w-4 h-2 rounded-full" style={{ backgroundColor: getRecipeTimelineColor(0) }} />
                                        {t('prepTimelineActiveWork')}
                                    </div>
                                    <div className={`flex items-center gap-2 ${rtl ? 'flex-row-reverse' : ''}`}>
                                        <span
                                            className="inline-block w-4 h-2 rounded-full opacity-50"
                                            style={{
                                                backgroundColor: getRecipeTimelineColor(0),
                                                backgroundImage: TIMELINE_WAIT_HATCH,
                                            }}
                                        />
                                        {t('prepTimelinePassiveWait')}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ConfirmationModal
                isOpen={isStopConfirmOpen}
                title={t('prepTimelineStopSessionConfirmTitle')}
                message={t('prepTimelineStopSessionConfirmMessage')}
                confirmText={t('prepTimelineStopSession')}
                cancelText={t('close')}
                type="warning"
                onConfirm={() => void handleStopSession()}
                onCancel={() => setIsStopConfirmOpen(false)}
            />

            {isAdjustStartChoiceOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[650] p-0 sm:p-4 animate-fade-in"
                    onClick={() => setIsAdjustStartChoiceOpen(false)}
                >
                    <div
                        className="bg-white dark:bg-slate-900 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl border border-stone-100 dark:border-slate-800 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800 text-center sm:text-start">
                            <h3 className="text-lg font-black text-slate-800 dark:text-slate-100">
                                {t('prepTimelineAdjustStartTitle')}
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
                                {t('prepTimelineAdjustStartMessage')}
                            </p>
                        </div>
                        <div className="p-4 space-y-2">
                            <button
                                type="button"
                                onClick={() => void handleResetStartToNow()}
                                disabled={isSavingSession}
                                className="w-full py-3 rounded-full text-xs font-black uppercase tracking-widest text-[#FDFBF7] disabled:opacity-50"
                                style={{ backgroundColor: INK }}
                            >
                                {t('prepTimelineResetToNow')}
                            </button>
                            <button
                                type="button"
                                onClick={() => openStartTimePicker('session')}
                                disabled={isSavingSession}
                                className="w-full py-3 rounded-full border text-xs font-black uppercase tracking-widest transition-colors hover:bg-stone-50 dark:hover:bg-slate-800 disabled:opacity-50"
                                style={{ borderColor: FRAME_LINE, color: INK }}
                            >
                                {t('prepTimelineSetActualStartTime')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsAdjustStartChoiceOpen(false)}
                                className="w-full py-3 rounded-full text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <StartTimePickerModal
                isOpen={startTimePickerPurpose !== null}
                title={t('prepTimelineStartTimeLabel')}
                value={startTimeDraft}
                saveLabel={t('prepTimelineApplyStartTime')}
                cancelLabel={t('cancel')}
                onChange={setStartTimeDraft}
                onSave={() => void handleSaveStartTime()}
                onCancel={() => setStartTimePickerPurpose(null)}
            />
        </div>
    );
};

export default PrepTimelineModal;
